import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faLock,
  faArrowUp,
  faArrowDown,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import { getApiUrl, SERVERS, ENDPOINTS } from '../config/api';
import './CustomFieldsManagement.css';

const CustomFieldsManagement = () => {
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingField, setEditingField] = useState(null);

  // Form state
  const [fieldForm, setFieldForm] = useState({
    field_name: '',
    field_label: '',
    field_type: 'select',
    is_required: false,
    options: ['']
  });

  useEffect(() => {
    fetchCustomFields();
  }, []);

  const fetchCustomFields = async () => {
    try {
      setLoading(true);
      const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.CUSTOM_FIELDS);
      const response = await fetch(`${url}?include_inactive=false`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setCustomFields(data);
      } else if (data.fields && Array.isArray(data.fields)) {
        setCustomFields(data.fields);
      } else {
        setCustomFields([]);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to fetch custom fields: ' + err.message);
      setCustomFields([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFieldForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...fieldForm.options];
    newOptions[index] = value;
    setFieldForm(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    setFieldForm(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    if (fieldForm.options.length > 1) {
      const newOptions = fieldForm.options.filter((_, i) => i !== index);
      setFieldForm(prev => ({ ...prev, options: newOptions }));
    }
  };

  const validateForm = () => {
    if (!fieldForm.field_name.trim()) {
      setError('Field name is required');
      return false;
    }
    if (!fieldForm.field_label.trim()) {
      setError('Field label is required');
      return false;
    }
    if (fieldForm.field_type === 'select') {
      const validOptions = fieldForm.options.filter(opt => opt.trim());
      if (validOptions.length === 0) {
        setError('At least one option is required for select fields');
        return false;
      }
    }
    return true;
  };

  const handleCreateField = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Clean up field_name: convert to snake_case
      const cleanFieldName = fieldForm.field_name
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

      // Filter out empty options
      const validOptions = fieldForm.options.filter(opt => opt.trim());

      const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.CUSTOM_FIELDS);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...fieldForm,
          field_name: cleanFieldName,
          options: validOptions,
          display_order: customFields.length
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Custom field created successfully!');
        setShowCreateForm(false);
        setFieldForm({
          field_name: '',
          field_label: '',
          field_type: 'select',
          is_required: false,
          options: ['']
        });
        fetchCustomFields();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.detail || 'Failed to create custom field');
      }
    } catch (err) {
      setError('Error creating custom field: ' + err.message);
    }
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setFieldForm({
      field_name: field.field_name,
      field_label: field.field_label,
      field_type: field.field_type,
      is_required: field.is_required,
      options: field.options.length > 0 ? field.options : ['']
    });
  };

  const handleUpdateField = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const validOptions = fieldForm.options.filter(opt => opt.trim());

      const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.CUSTOM_FIELD);
      const response = await fetch(`${url}/${editingField.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          field_label: fieldForm.field_label,
          options: validOptions,
          is_required: fieldForm.is_required
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Custom field updated successfully!');
        setEditingField(null);
        setFieldForm({
          field_name: '',
          field_label: '',
          field_type: 'select',
          is_required: false,
          options: ['']
        });
        fetchCustomFields();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.detail || 'Failed to update custom field');
      }
    } catch (err) {
      setError('Error updating custom field: ' + err.message);
    }
  };

  const handleDeleteField = async (fieldId, fieldLabel, isSystemField) => {
    if (isSystemField) {
      setError('System fields cannot be deleted!');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!confirm(`Are you sure you want to delete "${fieldLabel}"?`)) {
      return;
    }

    try {
      const url = getApiUrl(SERVERS.DATABASE, ENDPOINTS.CUSTOM_FIELD);
      const response = await fetch(`${url}/${fieldId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Custom field deleted successfully!');
        fetchCustomFields();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.detail || 'Failed to delete custom field');
      }
    } catch (err) {
      setError('Error deleting custom field: ' + err.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setFieldForm({
      field_name: '',
      field_label: '',
      field_type: 'select',
      is_required: false,
      options: ['']
    });
    setError(null);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading custom fields...</p>
      </div>
    );
  }

  return (
    <div className="custom-fields-management">
      <div className="cfm-header">
        <div>
          <h2>
            <FontAwesomeIcon icon={faCog} /> Custom Fields Management
          </h2>
          <p className="cfm-subtitle">
            Manage dynamic fields for program classification. System fields (marked with{' '}
            <FontAwesomeIcon icon={faLock} />) cannot be deleted but their options can be modified.
          </p>
        </div>
        {!showCreateForm && !editingField && (
          <button
            className="btn-create"
            onClick={() => setShowCreateForm(true)}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Custom Field
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error}
          <button onClick={() => setError(null)} className="alert-close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <FontAwesomeIcon icon={faCheckCircle} />
          {success}
          <button onClick={() => setSuccess(null)} className="alert-close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="cfm-form-card">
          <div className="cfm-form-header">
            <h3>Create New Custom Field</h3>
            <button
              className="btn-icon"
              onClick={() => {
                setShowCreateForm(false);
                setFieldForm({
                  field_name: '',
                  field_label: '',
                  field_type: 'select',
                  is_required: false,
                  options: ['']
                });
                setError(null);
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <form onSubmit={handleCreateField}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="field_name">
                  Field Name (Internal ID) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="field_name"
                  name="field_name"
                  value={fieldForm.field_name}
                  onChange={handleInputChange}
                  placeholder="e.g., specialization"
                  required
                />
                <small>Use lowercase letters, numbers, and underscores only</small>
              </div>

              <div className="form-group">
                <label htmlFor="field_label">
                  Field Label (Display Name) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="field_label"
                  name="field_label"
                  value={fieldForm.field_label}
                  onChange={handleInputChange}
                  placeholder="e.g., Specialization"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="field_type">Field Type</label>
                <select
                  id="field_type"
                  name="field_type"
                  value={fieldForm.field_type}
                  onChange={handleInputChange}
                  disabled
                >
                  <option value="select">Dropdown (Select)</option>
                </select>
                <small>Currently only dropdown fields are supported</small>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_required"
                    checked={fieldForm.is_required}
                    onChange={handleInputChange}
                  />
                  Required Field
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>
                Dropdown Options <span className="required">*</span>
              </label>
              <div className="options-list">
                {fieldForm.options.map((option, index) => (
                  <div key={index} className="option-item">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      required
                    />
                    {fieldForm.options.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove-option"
                        onClick={() => removeOption(index)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" className="btn-add-option" onClick={addOption}>
                <FontAwesomeIcon icon={faPlus} /> Add Option
              </button>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-save">
                <FontAwesomeIcon icon={faSave} /> Create Field
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowCreateForm(false);
                  setFieldForm({
                    field_name: '',
                    field_label: '',
                    field_type: 'select',
                    is_required: false,
                    options: ['']
                  });
                  setError(null);
                }}
              >
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Form */}
      {editingField && (
        <div className="cfm-form-card">
          <div className="cfm-form-header">
            <h3>
              Edit Custom Field: {editingField.field_label}
              {editingField.is_system_field && (
                <span className="system-badge">
                  <FontAwesomeIcon icon={faLock} /> System Field
                </span>
              )}
            </h3>
            <button className="btn-icon" onClick={handleCancelEdit}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <form onSubmit={handleUpdateField}>
            <div className="form-grid">
              <div className="form-group">
                <label>Field Name (Cannot be changed)</label>
                <input
                  type="text"
                  value={fieldForm.field_name}
                  disabled
                  className="input-disabled"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit_field_label">
                  Field Label <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="edit_field_label"
                  name="field_label"
                  value={fieldForm.field_label}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Field Type (Cannot be changed)</label>
                <input
                  type="text"
                  value="Dropdown (Select)"
                  disabled
                  className="input-disabled"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_required"
                    checked={fieldForm.is_required}
                    onChange={handleInputChange}
                  />
                  Required Field
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>
                Dropdown Options <span className="required">*</span>
              </label>
              <div className="options-list">
                {fieldForm.options.map((option, index) => (
                  <div key={index} className="option-item">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      required
                    />
                    {fieldForm.options.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove-option"
                        onClick={() => removeOption(index)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" className="btn-add-option" onClick={addOption}>
                <FontAwesomeIcon icon={faPlus} /> Add Option
              </button>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-save">
                <FontAwesomeIcon icon={faSave} /> Update Field
              </button>
              <button type="button" className="btn-cancel" onClick={handleCancelEdit}>
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Fields List */}
      {!showCreateForm && !editingField && (
        <div className="fields-list">
          {customFields.length === 0 ? (
            <div className="empty-state">
              <p>No custom fields found. Click "Add Custom Field" to create one.</p>
            </div>
          ) : (
            <div className="fields-grid">
              {customFields.map((field) => (
                <div key={field.id} className="field-card">
                  <div className="field-card-header">
                    <div>
                      <h4>
                        {field.field_label}
                        {field.is_required && <span className="required-badge">Required</span>}
                        {field.is_system_field && (
                          <span className="system-badge">
                            <FontAwesomeIcon icon={faLock} /> System
                          </span>
                        )}
                      </h4>
                      <p className="field-name">Field Name: {field.field_name}</p>
                    </div>
                    <div className="field-actions">
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleEditField(field)}
                        title="Edit field"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      {!field.is_system_field && (
                        <button
                          className="btn-icon btn-delete"
                          onClick={() =>
                            handleDeleteField(field.id, field.field_label, field.is_system_field)
                          }
                          title="Delete field"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="field-card-body">
                    <div className="field-info">
                      <span className="field-type-badge">{field.field_type}</span>
                      <span className="field-order">Order: {field.display_order}</span>
                    </div>

                    {field.options && field.options.length > 0 && (
                      <div className="field-options">
                        <strong>Options:</strong>
                        <div className="options-tags">
                          {field.options.map((opt, idx) => (
                            <span key={idx} className="option-tag">
                              {opt}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomFieldsManagement;
